/**
 * Convert a post body_md into React nodes:
 * • Extracts markdown-style image references `![alt](url)` and renders them
 *   as responsive `<img>` tags.
 * • Renders plain text with whitespace preserved (whitespace-pre-wrap).
 *
 * Intentionally tiny — the forum body is short-form, not a full CMS.
 */
import React from 'react';

const IMG_RE = /!\[([^\]]*)\]\(([^)\s]+)\)/g;

export function renderForumBody(bodyMd, images = []) {
  const nodes = [];
  const text = bodyMd || '';
  let lastIdx = 0;
  let m;
  IMG_RE.lastIndex = 0;
  while ((m = IMG_RE.exec(text)) !== null) {
    if (m.index > lastIdx) {
      nodes.push({ type: 'text', content: text.slice(lastIdx, m.index) });
    }
    nodes.push({ type: 'img', alt: m[1] || 'attachment', url: m[2] });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) {
    nodes.push({ type: 'text', content: text.slice(lastIdx) });
  }

  return (
    <div className="space-y-3">
      {nodes.map((n, i) => n.type === 'text'
        ? (n.content.trim() && (
            <p key={i} className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{n.content}</p>
          ))
        : (
            <a
              key={i}
              href={n.url}
              target="_blank"
              rel="noreferrer"
              className="block rounded-xl overflow-hidden border border-border bg-muted hover:opacity-95 transition-opacity"
              data-testid="forum-body-image"
            >
              <img src={n.url} alt={n.alt} className="w-full max-h-[520px] object-contain bg-black/5" loading="lazy" />
            </a>
          )
      )}
      {/* Extra unreferenced images (from images[] not embedded via markdown) */}
      {(images || []).filter((img) => !text.includes(img.url)).map((img, i) => (
        <a
          key={`extra-${i}`}
          href={img.url}
          target="_blank"
          rel="noreferrer"
          className="block rounded-xl overflow-hidden border border-border bg-muted hover:opacity-95 transition-opacity"
          data-testid="forum-body-image"
        >
          <img src={img.url} alt={img.filename || 'attachment'} className="w-full max-h-[520px] object-contain bg-black/5" loading="lazy" />
        </a>
      ))}
    </div>
  );
}
